import { useState, useCallback } from "react"
import {
    Badge, Box, Button, Flex, Input, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Spinner,
    Td, Text, useColorModeValue, useDisclosure, useToast
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Row, Table, Tag } from "components"
import { API } from "config"
import { useAllDistributors } from "hooks"

const useInventory = (distributorId, search, page) => useQuery({
    queryKey: ['stock-inventory', distributorId, search, page],
    queryFn: async () => {
        const params = new URLSearchParams({ page, per_page: 30 })
        if (distributorId) params.set('distributor_id', distributorId)
        if (search)        params.set('search', search)
        const { data } = await API.get(`/stock/inventory?${params}`)
        return data
    },
    staleTime: 15000,
})

const useBlockedDetail = (productId, distributorId) => useQuery({
    queryKey: ['stock-blocked-detail', productId, distributorId],
    queryFn: async () => {
        const { data } = await API.get(`/stock/blocked-detail?product_id=${productId}&distributor_id=${distributorId}`)
        return data
    },
    enabled: !!productId && !!distributorId,
    staleTime: 10000,
})

function BlockedDetailModal({ isOpen, onClose, productName, productId, distributorId, onRepaired }) {
    const { data, isFetching, refetch } = useBlockedDetail(
        isOpen ? productId : null,
        isOpen ? distributorId : null,
    )
    const [repairing, setRepairing] = useState(false)
    const toast      = useToast()
    const cardBg     = useColorModeValue('white',    'gray.800')
    const labelColor = useColorModeValue('gray.500', 'gray.400')
    const rowBg      = useColorModeValue('orange.50', 'gray.750')
    const rowBorder  = useColorModeValue('orange.100','orange.800')

    const handleRepair = async () => {
        setRepairing(true)
        try {
            const res = await API.post('/stock/repair-blocked', { product_id: productId, distributor_id: distributorId })
            toast({ title: res.data.message, status: 'success', duration: 5000, isClosable: true })
            onRepaired?.()
            onClose()
        } catch (err) {
            toast({ title: err?.response?.data?.message ?? 'Erro ao corrigir bloqueio', status: 'error', isClosable: true })
        } finally {
            setRepairing(false)
        }
    }

    const hasOrphanedBlock = !isFetching && data && data.invoices?.length === 0

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
            <ModalOverlay />
            <ModalContent bg={cardBg}>
                <ModalHeader fontSize="md">
                    <Flex align="center" gap={2}>
                        <Icon name="invoice" boxSize={5} color="orange.400" />
                        Estoque bloqueado — {productName}
                    </Flex>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={2}>
                    <Text fontSize="sm" color={labelColor} mb={4}>
                        Itens abaixo são de NF-es registradas que ainda aguardam conferência física.
                        O estoque só é liberado após o recebimento ser confirmado na tela da nota.
                    </Text>

                    {isFetching ? (
                        <Flex justify="center" py={8}><Spinner /></Flex>
                    ) : !data?.invoices?.length ? (
                        <Flex direction="column" align="center" gap={2} py={8} color={labelColor}>
                            <Icon name="check" boxSize={8} color="green.300" />
                            <Text fontSize="sm" fontWeight="medium">Nenhuma NF-e pendente encontrada.</Text>
                            <Text fontSize="xs" textAlign="center" maxW="320px">
                                O contador de bloqueio está desatualizado (dado anterior ao novo sistema).
                                Você pode corrigi-lo com segurança clicando no botão abaixo.
                            </Text>
                        </Flex>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={3}>
                            {data.invoices.map((inv, i) => (
                                <Box key={i} bg={rowBg} border="1px solid" borderColor={rowBorder}
                                    borderRadius="xl" p={4}>
                                    <Flex justify="space-between" align="flex-start" mb={3}>
                                        <Box>
                                            <Text fontWeight="semibold" fontSize="sm">
                                                NF-e #{inv.invoice_number ?? '—'}
                                            </Text>
                                            <Text fontSize="xs" color={labelColor}>{inv.supplier_name ?? '—'}</Text>
                                        </Box>
                                        <Tag colorScheme="orange" size="sm">
                                            {inv.pending_qty} un. pendentes
                                        </Tag>
                                    </Flex>
                                    <Flex gap={4} flexWrap="wrap">
                                        {[
                                            { label: 'Pedido na NF-e', value: `${inv.ordered_qty} un.` },
                                            { label: 'Já recebido',    value: `${inv.received_qty} un.` },
                                            { label: 'Pendente',       value: `${inv.pending_qty} un.` },
                                            { label: 'Entrada em',     value: inv.created_at ? new Date(inv.created_at).toLocaleDateString('pt-BR') : '—' },
                                        ].map(({ label, value }) => (
                                            <Box key={label}>
                                                <Text fontSize="2xs" textTransform="uppercase"
                                                    letterSpacing="wider" color={labelColor} fontWeight="semibold">
                                                    {label}
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium">{value}</Text>
                                            </Box>
                                        ))}
                                    </Flex>
                                </Box>
                            ))}
                        </Box>
                    )}
                </ModalBody>
                {hasOrphanedBlock && (
                    <ModalFooter borderTop="1px solid" borderColor={rowBorder} mt={2}>
                        <Button colorScheme="orange" onClick={handleRepair} isLoading={repairing}
                            leftIcon={<Icon name="edit" boxSize={4} />} size="md">
                            Corrigir contador de bloqueio
                        </Button>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    )
}

export default function Inventory() {
    const [distributorId, setDistributorId] = useState('')
    const [search, setSearch]               = useState('')
    const [debouncedSearch, setDebounced]   = useState('')
    const [page, setPage]                   = useState(1)
    const [blockedItem, setBlockedItem]     = useState(null)
    const blockedModal                      = useDisclosure()

    const distributors = useAllDistributors()
    const warehouses   = distributors.data?.filter(d => d.is_warehouse) ?? []

    const { data, isFetching, refetch } = useInventory(distributorId, debouncedSearch, page)
    const items = data?.data ?? []

    const filterBg     = useColorModeValue('gray.50',  'gray.700')
    const filterBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor   = useColorModeValue('gray.500', 'gray.400')

    const handleSearch = (v) => {
        setSearch(v)
        clearTimeout(window._invSearchTimeout)
        window._invSearchTimeout = setTimeout(() => { setDebounced(v); setPage(1) }, 350)
    }

    const openBlocked = (item) => {
        setBlockedItem(item)
        blockedModal.onOpen()
    }

    const columns = [
        { children: 'Produto' },
        { children: 'Núcleo' },
        { children: 'Disponível', isNumeric: true },
        { children: 'Reservado', isNumeric: true },
        { children: 'Bloqueado', isNumeric: true },
        { children: 'Total físico', isNumeric: true },
    ]

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Inventário de Estoque" />

            <Box bg={filterBg} border="1px solid" borderColor={filterBorder} borderRadius="xl" p={4} mt={5} mb={5}>
                <Flex align="center" gap={2} mb={3}>
                    <Icon name="search" boxSize={3.5} color={labelColor} />
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                        letterSpacing="widest" color={labelColor}>
                        Filtros
                    </Text>
                </Flex>
                <Flex gap={3} flexWrap="wrap" align="flex-end">
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Núcleo
                        </Text>
                        <Select size="md" w="260px" value={distributorId}
                            onChange={e => { setDistributorId(e.target.value); setPage(1) }}>
                            <option value="">Todos os núcleos</option>
                            {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Select>
                    </Box>
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Buscar produto
                        </Text>
                        <Input size="md" w="300px" value={search}
                            placeholder="Nome ou código..."
                            onChange={e => handleSearch(e.target.value)} />
                    </Box>
                    <IconButton size="md" icon="refresh" isLoading={isFetching} onClick={refetch} />
                </Flex>
            </Box>

            {isFetching && !items.length ? (
                <Flex justify="center" py={16}><Spinner size="lg" /></Flex>
            ) : items.length === 0 ? (
                <Flex justify="center" align="center" direction="column" gap={3} py={16}>
                    <Icon name="stock" boxSize={12} color="gray.300" />
                    <Text fontSize="sm" color="gray.400">
                        {debouncedSearch
                            ? `Nenhum produto encontrado para "${debouncedSearch}".`
                            : 'Nenhum registro de estoque encontrado.'}
                    </Text>
                </Flex>
            ) : (
                <Table columns={columns} setPage={setPage} meta={{ current_page: data?.current_page, last_page: data?.last_page, total: data?.total }}>
                    {items.map(item => (
                        <Row key={item.id}>
                            <Td>
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold">{item.product?.name}</Text>
                                    {item.product?.cod && (
                                        <Text fontSize="xs" color="gray.400">Cód: {item.product.cod}</Text>
                                    )}
                                </Box>
                            </Td>
                            <Td>
                                <Tag colorScheme="blue" size="sm">{item.distributor?.name ?? '—'}</Tag>
                            </Td>
                            <Td isNumeric>
                                <Text fontWeight="bold" fontSize="sm"
                                    color={item.available <= 5 ? 'orange.500' : 'green.500'}>
                                    {item.available}
                                </Text>
                            </Td>
                            <Td isNumeric>
                                <Text fontSize="sm" color={item.reserved > 0 ? 'yellow.500' : 'gray.400'}>
                                    {item.reserved}
                                </Text>
                            </Td>
                            <Td isNumeric>
                                {item.blocked > 0 ? (
                                    <Badge
                                        colorScheme="purple"
                                        fontSize="xs"
                                        cursor="pointer"
                                        _hover={{ opacity: 0.75 }}
                                        title="Clique para ver quais NF-es estão bloqueando"
                                        onClick={() => openBlocked(item)}
                                    >
                                        {item.blocked} ⓘ
                                    </Badge>
                                ) : (
                                    <Text fontSize="sm" color="gray.400">—</Text>
                                )}
                            </Td>
                            <Td isNumeric>
                                <Text fontSize="sm" fontWeight="medium">{item.stock}</Text>
                            </Td>
                        </Row>
                    ))}
                </Table>
            )}

            <BlockedDetailModal
                isOpen={blockedModal.isOpen}
                onClose={blockedModal.onClose}
                productName={blockedItem?.product?.name}
                productId={blockedItem?.product?.id}
                distributorId={blockedItem?.distributor?.id}
                onRepaired={refetch}
            />
        </Container>
    )
}
